import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ConfirmModal from './ConfirmModal';
import ToastViewport from './ToastViewport';
import { registerNotificationHandlers, unregisterNotificationHandlers } from './bridge';
import { DEDUPE_WINDOW_MS, MAX_TOASTS, resolveToastDuration } from './utils';
import type { ConfirmRequest, ShowConfirmOptions, ShowToastOptions, ToastItem } from './types';

type NotificationContextValue = {
  showToast: (options: ShowToastOptions) => string;
  showConfirm: (options: ShowConfirmOptions) => Promise<boolean>;
  dismissToast: (id: string) => void;
};

const NotificationContext = createContext<NotificationContextValue | null>(null);

type TimerState = {
  remaining: number;
  startedAt: number | null;
  paused: boolean;
  timeoutId: number | null;
};

function createId(): string {
  return `ntf_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [confirmRequest, setConfirmRequest] = useState<ConfirmRequest | null>(null);
  const timersRef = useRef<Map<string, TimerState>>(new Map());
  const recentRef = useRef<Map<string, number>>(new Map());

  const clearTimer = useCallback((id: string) => {
    const state = timersRef.current.get(id);
    if (state?.timeoutId) window.clearTimeout(state.timeoutId);
    timersRef.current.delete(id);
  }, []);

  const dismissToast = useCallback((id: string) => {
    clearTimer(id);
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, [clearTimer]);

  const armTimer = useCallback((id: string) => {
    const state = timersRef.current.get(id);
    if (!state || state.remaining <= 0 || state.paused) return;

    if (state.timeoutId) window.clearTimeout(state.timeoutId);
    state.startedAt = Date.now();
    state.timeoutId = window.setTimeout(() => {
      dismissToast(id);
    }, state.remaining);
  }, [dismissToast]);

  const showToast = useCallback((options: ShowToastOptions) => {
    const type = options.type || 'info';
    const title = options.title?.trim() || 'Notificação';
    const message = options.message?.trim() || undefined;
    const duration = resolveToastDuration(type, options.duration);
    const dedupeKey = `${type}|${title}|${message || ''}`;
    const now = Date.now();
    const last = recentRef.current.get(dedupeKey);
    if (last && now - last < DEDUPE_WINDOW_MS) {
      return '';
    }
    recentRef.current.set(dedupeKey, now);

    const id = createId();
    const item: ToastItem = {
      id,
      type,
      title,
      message,
      duration,
      createdAt: now,
    };

    setToasts((prev) => {
      const next = [...prev, item];
      if (next.length <= MAX_TOASTS) return next;
      const removed = next.slice(0, next.length - MAX_TOASTS);
      removed.forEach((toast) => clearTimer(toast.id));
      return next.slice(next.length - MAX_TOASTS);
    });

    if (duration > 0) {
      timersRef.current.set(id, {
        remaining: duration,
        startedAt: null,
        paused: false,
        timeoutId: null,
      });
      // Arm after state commit
      window.setTimeout(() => armTimer(id), 0);
    }

    return id;
  }, [armTimer, clearTimer]);

  const pauseToast = useCallback((id: string, paused: boolean) => {
    const state = timersRef.current.get(id);
    if (!state || state.remaining <= 0) return;

    if (paused) {
      if (state.paused) return;
      if (state.timeoutId) window.clearTimeout(state.timeoutId);
      if (state.startedAt != null) {
        const elapsed = Date.now() - state.startedAt;
        state.remaining = Math.max(0, state.remaining - elapsed);
      }
      state.timeoutId = null;
      state.startedAt = null;
      state.paused = true;
      return;
    }

    if (!state.paused) return;
    state.paused = false;
    armTimer(id);
  }, [armTimer]);

  const showConfirm = useCallback((options: ShowConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setConfirmRequest((current) => {
        if (current) {
          current.resolve(false);
        }
        return {
          ...options,
          resolve,
        };
      });
    });
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmRequest(null);
  }, []);

  useEffect(() => {
    registerNotificationHandlers({ showToast, showConfirm });
    return () => {
      unregisterNotificationHandlers();
      timersRef.current.forEach((state) => {
        if (state.timeoutId) window.clearTimeout(state.timeoutId);
      });
      timersRef.current.clear();
    };
  }, [showToast, showConfirm]);

  const value = useMemo(
    () => ({ showToast, showConfirm, dismissToast }),
    [showToast, showConfirm, dismissToast],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismissToast} onPause={pauseToast} />
      <ConfirmModal request={confirmRequest} onClose={closeConfirm} />
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications deve ser usado dentro de NotificationProvider.');
  }
  return ctx;
}

export function useToast() {
  const { showToast, dismissToast } = useNotifications();
  return { showToast, dismissToast };
}

export function useConfirm() {
  const { showConfirm } = useNotifications();
  return { showConfirm };
}
